"use client";

import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { SpecimenLabel } from "@/components/ui/specimen-label";
import { HOME_CONTENT } from "@/content/home";
import { SITE } from "@/content/site";

export function Hero() {
  const { hero } = HOME_CONTENT;
  const [titlePrefix, titleSuffix] = hero.title.split(hero.emphasis);

  return (
    <header className="home-hero relative flex min-h-[560px] overflow-hidden bg-forest-900 px-5 pb-16 text-paper-inverse">
      <div
        role="img"
        aria-label="老挝高地山林晨雾"
        className="home-hero-background absolute inset-0"
      >
        <Image
          src="/images/generated/home-forest-hero-v1.webp"
          alt=""
          fill
          sizes="(max-width: 1200px) 100vw, 1200px"
          preload
          className="object-cover object-center"
        />
        <span aria-hidden="true" className="home-hero-scrim absolute inset-0" />
      </div>
      <span
        aria-hidden="true"
        className="absolute top-24 right-[18px] font-display text-xl font-black tracking-[0.42em] text-paper-inverse/15 [writing-mode:vertical-rl]"
      >
        {hero.watermark}
      </span>

      <div className="relative z-10 flex min-w-0 flex-1 flex-col">
        <div className="home-hero-rise flex items-start justify-between gap-3 pt-[18px]">
          <div className="min-w-0 font-display text-[17px] leading-6 font-black tracking-[0.14em] text-paper-inverse">
                <span className="mt-[3px] block font-mono text-[9px] leading-3 font-medium tracking-[0.18em] text-paper-inverse/55">
              {SITE.nameEn} · EST. LAOS
            </span>
          </div>
          <Link
            href={hero.memberEntry.href}
            className="flex min-h-11 shrink-0 items-center border border-paper-inverse/40 px-2.5 font-mono text-[10px] leading-4 tracking-[0.14em] text-paper-inverse/75 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-paper-inverse"
          >
            {hero.memberEntry.label}
          </Link>
        </div>

        <div className="home-hero-copy home-hero-rise mt-[78px]">
          <p className="font-mono text-[11px] leading-4 tracking-[0.22em] text-paper-inverse/60">
            {hero.kicker}
          </p>
          <h1 className="mt-3.5 font-display text-[29px] leading-[1.4] font-black tracking-[0.02em] text-paper-inverse">
            {titlePrefix}
            <em className="home-hero-emphasis not-italic">{hero.emphasis}</em>
            {titleSuffix.slice(0, 1)}
            <br />
            {titleSuffix.slice(1)}
          </h1>
          <p className="mt-3.5 max-w-[30em] text-sm leading-[1.8] text-paper-inverse/80">
            {hero.description}
          </p>
          <div className="mt-[26px] flex gap-3">
            <Button
              variant="fill-inverse"
              href={hero.primaryAction.href}
              className="min-w-0 flex-1 px-3"
            >
              {hero.primaryAction.label}
            </Button>
            <Button
              variant="outline-inverse"
              href={hero.secondaryAction.href}
              className="min-w-0 flex-1 px-3"
            >
              {hero.secondaryAction.label}
            </Button>
          </div>
          <p className="mt-3.5 text-xs leading-[18px] text-paper-inverse/60">
            {hero.memberPrefix}
            <Link
              href={hero.memberAction.href}
              className="ml-1 min-h-11 py-3 text-paper-inverse/85 underline underline-offset-[3px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-paper-inverse"
            >
              {hero.memberAction.label}
            </Link>
          </p>
        </div>

        <div className="mt-auto flex items-end justify-between gap-3 pt-[34px]">
          <SpecimenLabel
            code={hero.origin.code}
            name={hero.origin.name}
            variant="inverse"
            className="home-hero-stamp min-w-0"
          />
          <span className="shrink-0 pb-1 text-right font-mono text-[9px] leading-4 tracking-[0.14em] text-paper-inverse/55">
            <span className="block tracking-[0.2em]">{hero.scrollLabel}</span>
          </span>
        </div>
      </div>

      <style>{`
        .home-hero-background {
          background: var(--c-forest-900);
        }
        .home-hero-scrim {
          background:
            linear-gradient(180deg, color-mix(in srgb, var(--c-forest-900) 76%, transparent) 0%, color-mix(in srgb, var(--c-forest-900) 44%, transparent) 38%, color-mix(in srgb, var(--c-forest-900) 76%, transparent) 100%),
            radial-gradient(120% 70% at 80% 0%, color-mix(in srgb, var(--c-forest-500) 55%, transparent), transparent 60%),
            radial-gradient(90% 60% at 10% 90%, color-mix(in srgb, var(--c-tea) 18%, transparent), transparent 55%),
            linear-gradient(160deg, color-mix(in srgb, var(--c-forest-700) 42%, transparent) 0%, color-mix(in srgb, var(--c-forest-900) 36%, transparent) 55%, color-mix(in srgb, var(--c-forest-900) 74%, transparent) 100%);
        }
        .home-hero-background::after {
          content: "";
          position: absolute;
          inset: 0;
          opacity: .5;
          background:
            repeating-linear-gradient(0deg, transparent 0 2px, color-mix(in srgb, var(--c-paper-inverse) 1.2%, transparent) 2px 4px),
            repeating-linear-gradient(90deg, transparent 0 2px, color-mix(in srgb, var(--c-forest-900) 2%, transparent) 2px 4px);
        }
        .home-hero-emphasis {
          color: color-mix(in srgb, var(--c-gilt) 65%, var(--c-paper-inverse));
        }
        @media (prefers-reduced-motion: no-preference) {
          .home-hero-background {
            opacity: 1;
            animation: home-hero-scrim 400ms ease-out forwards;
          }
          .home-hero-rise {
            opacity: 1;
            animation: home-hero-rise 500ms ease-out 150ms forwards;
          }
          .home-hero-stamp {
            opacity: 1;
            transform: scale(1.06) rotate(-2deg);
            animation: home-hero-stamp 250ms ease-out 500ms forwards;
          }
          @keyframes home-hero-scrim { to { opacity: 1; } }
          @keyframes home-hero-rise {
            from { opacity: 1; transform: translateY(12px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes home-hero-stamp {
            to { opacity: 1; transform: scale(1) rotate(0); }
          }
        }
      `}</style>
    </header>
  );
}
